import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Currency } from "@nh/shared";
import { SiteFooter, SiteHeader } from "@/shared/ui/site";
import { AnnouncementBar } from "./AnnouncementBar";
import { CartPanel, useCart } from "@/features/cart";
import { useAuth } from "@/features/auth/auth.context";
import { useCurrencyIsLocked } from "../useShopCurrency";
import { localeOf } from "../shop.catalog";

export interface ShopLayoutProps {
  currency: Currency;
  onCurrencyChange: (currency: Currency) => void;
  children: ReactNode;
}

/**
 * El armazón de cualquier pantalla PÚBLICA de la tienda: barra superior,
 * contenido, pie y el cajón del carrito.
 *
 * ── Por qué existe ───────────────────────────────────────────────────
 * `ShopPage` y `ProductDetailPage` traían las mismas cinco líneas de
 * cromo, y con el carrito habrían traído tres más — el contador, el
 * `onCartClick` y el panel. Dos copias significa que el día que el
 * carrito aprenda algo nuevo, lo aprenda en una pantalla y en la otra no.
 *
 * ── Es quien conecta el carrito con la barra ─────────────────────────
 * `SiteHeader` no consulta el contexto del carrito a propósito (se monta
 * en una prueba sin levantarlo, y el panel de administración usa la misma
 * barra). El puente es este componente: lee `useCart()` y le pasa el
 * contador y el "abrir" como props. El contador que se ve es el carrito
 * de verdad, no un número de adorno.
 *
 * ── La moneda la manda la página ─────────────────────────────────────
 * Y no este armazón. La tienda necesita la moneda para ORDENAR por
 * precio, así que el estado tiene que vivir por encima de la rejilla;
 * duplicarlo aquí crearía dos fuentes de verdad para la misma
 * preferencia.
 */
export function ShopLayout({ currency, onCurrencyChange, children }: ShopLayoutProps) {
  /*
   * Con país elegido, la moneda ya está decidida y el selector sobra.
   * Ver `useShopCurrency`: el país manda sobre idioma, moneda y cuenta de
   * Stripe a la vez, que es lo que evita cobrar en la cuenta equivocada.
   */
  const currencyLocked = useCurrencyIsLocked();
  const { i18n } = useTranslation();

  /*
   * ── Cada pantalla abre POR ARRIBA (Anahí, 29-ago) ──────────────────
   * "Me lanza /franquicias/adquirir pero aparece así" —a media página,
   * enseñando el pie—. El navegador conserva el desplazamiento al
   * cambiar de ruta, así que pulsar "elegir este paquete" desde el fondo
   * de /paquetes dejaba la pantalla nueva empezada por la mitad: parecía
   * rota antes de haberla leído.
   *
   * Va aquí y no en cada página porque el problema es del armazón: le
   * pasa a toda pantalla pública a la que se llegue desde abajo.
   *
   * `pathname` a secas, sin `search`: cambiar de paquete en la misma
   * pantalla no tiene por qué mandarte arriba.
   */
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  const locale = localeOf(i18n.language);
  const { count, open } = useCart();
  /*
   * El puente de SESIÓN, igual que el del carrito: la barra no consulta
   * el contexto — este armazón le pasa quién entró y cómo salir. Es lo
   * que hace que entrar SE VEA (el saludo por nombre) en vez de dejar
   * la tienda idéntica a como estaba (Anahí, 26-ago).
   */
  const { user, logout } = useAuth();

  return (
    /*
     * `overflow-x-clip` en el contenedor de la página: los halos
     * decorativos son círculos que se salen a propósito por los lados, y
     * sin esto un teléfono ganaría una barra de desplazamiento horizontal
     * por un adorno que nadie quería ver.
     *
     * `clip` y NO `hidden`: los dos recortan igual, pero `hidden`
     * convierte al contenedor en scroll container y eso mata el
     * `position: sticky` de la barra — con la píldora flotante se notó
     * de inmediato: la barra se iba con el scroll.
     */
    <div className="flex min-h-screen flex-col overflow-x-clip bg-ds-cream">
      {/* El anuncio de la casa, si el panel lo encendió. */}
      <AnnouncementBar />
      <SiteHeader
        currency={currency}
        onCurrencyChange={onCurrencyChange}
        currencyLocked={currencyLocked}
        cartCount={count}
        onCartClick={open}
        user={
          user
            ? {
                name: user.firstName || user.fullName,
                // La puerta a SU panel, solo para quien lo tiene.
                /*
                 * TODOS tienen panel (Anahí, 27-ago): franquiciataria a
                 * su negocio, administración al suyo, y cualquier otra
                 * sesión a su panel de SOCIO — nadie con sesión se queda
                 * viendo la tienda como visitante.
                 */
                businessTo: user.franchiseeSince
                  ? "/mi-negocio"
                  : user.role === "super_admin" || user.role === "admin"
                    ? "/admin"
                    : "/socio",
              }
            : null
        }
        onLogout={() => void logout()}
      />

      <main className="flex-1">{children}</main>

      <SiteFooter />

      <CartPanel currency={currency} locale={locale} />
    </div>
  );
}
